import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';

const ALLOWED_TYPES = { 'image/jpeg': true, 'image/png': true, 'image/gif': true };
const ALLOWED_EXT   = /\.(jpg|jpeg|png|gif)$/i;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: 'America/Detroit',
  });
}

function ConfirmDeleteModal({ filename, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{ width: '100%', maxWidth: 380, padding: 24, textAlign: 'center' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
        <h3 style={{ margin: '0 0 8px' }}>Remove this image?</h3>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--muted)', wordBreak: 'break-all' }}>
          <strong>{filename}</strong>
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--muted)' }}>
          This image will be removed from the rotation. This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ flex: 1, fontSize: 14, padding: '8px 0' }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm"
            style={{ flex: 1, fontSize: 14, padding: '8px 0', background: 'var(--danger, #dc2626)', color: '#fff', border: 'none' }}
            onClick={onConfirm}
          >
            Yes, Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageCard({ img, onDelete }) {
  const [showModal, setShowModal] = useState(false);
  const isNeverUsed = !img.last_used_at;
  const isNext      = img._is_next;

  return (
    <>
      {showModal && (
        <ConfirmDeleteModal
          filename={img.filename}
          onConfirm={() => { setShowModal(false); onDelete(img.image_key); }}
          onCancel={() => setShowModal(false)}
        />
      )}
      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        {isNext && (
          <div style={{
            position: 'absolute', top: 8, left: 8, zIndex: 2,
            background: 'var(--primary)', color: '#fff',
            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
          }}>
            NEXT UP
          </div>
        )}
        {/* Thumbnail */}
        <div style={{
          height: 160, background: 'var(--surface)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderBottom: '1px solid var(--border)',
        }}>
          {img.preview_url
            ? <img
                src={img.preview_url}
                alt={img.filename}
                style={{ maxHeight: 150, maxWidth: '100%', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
              />
            : null
          }
          <div style={{ display: img.preview_url ? 'none' : 'flex', fontSize: 40 }}>🖼️</div>
        </div>

        {/* Meta */}
        <div style={{ padding: '10px 12px' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4, wordBreak: 'break-all' }}>
            {img.filename}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>
            Uploaded: {formatDate(img.upload_date)}
          </div>
          <div style={{ fontSize: 11, color: isNeverUsed ? 'var(--success, #16a34a)' : 'var(--muted)' }}>
            {isNeverUsed ? 'Never used' : `Last used: ${formatDate(img.last_used_at)}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            Used {img.use_count || 0} time{img.use_count === 1 ? '' : 's'}
          </div>
        </div>

        {/* Delete */}
        <div style={{ padding: '0 12px 12px' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: 'var(--danger, #dc2626)', width: '100%' }}
            onClick={() => setShowModal(true)}
          >
            Remove
          </button>
        </div>
      </div>
    </>
  );
}

// Upload a single file to S3 via presigned URL using XHR for progress tracking.
// onProgress(pct) called 0-100 during transfer.
function xhrUpload(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload  = () => xhr.status < 400 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

export default function BrandImagesPage() {
  const [images, setImages]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [dragOver, setDragOver]     = useState(false);
  // uploadQueue: array of { name, pct, done, error }
  const [uploadQueue, setUploadQueue] = useState([]);
  const fileRef = useRef();
  const { showToast } = useToast();

  const isUploading = uploadQueue.some(f => !f.done && !f.error);
  const allDone     = uploadQueue.length > 0 && uploadQueue.every(f => f.done || f.error);
  const overallPct  = uploadQueue.length === 0 ? 0
    : Math.round(uploadQueue.reduce((s, f) => s + (f.done ? 100 : f.pct), 0) / uploadQueue.length);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await api.getBrandImages();
      const sorted = [...items].sort((a, b) => (a.last_used_at || '').localeCompare(b.last_used_at || ''));
      setImages(items.map(img => ({ ...img, _is_next: sorted[0]?.image_key === img.image_key })));
    } catch (e) {
      showToast('Failed to load images: ' + e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  function setFilePct(name, pct)   { setUploadQueue(q => q.map(f => f.name === name ? { ...f, pct } : f)); }
  function setFileDone(name)       { setUploadQueue(q => q.map(f => f.name === name ? { ...f, pct: 100, done: true } : f)); }
  function setFileError(name, msg) { setUploadQueue(q => q.map(f => f.name === name ? { ...f, error: msg } : f)); }

  async function uploadFile(file) {
    if (!ALLOWED_EXT.test(file.name)) {
      setFileError(file.name, 'Only JPEG, PNG, GIF allowed');
      return;
    }
    if (!ALLOWED_TYPES[file.type]) {
      setFileError(file.name, 'Unsupported file type');
      return;
    }
    try {
      const { upload_url, key, filename } = await api.getBrandImageUploadUrl(file.name, file.type);
      await xhrUpload(upload_url, file, (pct) => setFilePct(file.name, pct));
      await api.confirmBrandImageUpload({ key, filename, content_type: file.type });
      setFileDone(file.name);
    } catch (e) {
      setFileError(file.name, e.message);
      showToast(`Upload failed: ${e.message}`, 'error');
    }
  }

  async function handleFiles(files) {
    if (!files?.length) return;
    const fileArr = Array.from(files);
    // Seed the queue with 0% entries so the bar appears immediately
    setUploadQueue(fileArr.map(f => ({ name: f.name, pct: 0, done: false, error: null })));
    await Promise.all(fileArr.map(uploadFile));
    await load();
    // Keep the completed bar visible briefly, then clear
    setTimeout(() => setUploadQueue([]), 2500);
  }

  async function handleDelete(key) {
    // Optimistic update — remove card instantly so the user sees it gone immediately
    setImages(prev => prev.filter(img => img.image_key !== key));
    try {
      await api.deleteBrandImage(key);
      showToast('Image removed', 'success');
      // Background sync to re-calculate NEXT UP badge after removal
      load();
    } catch (e) {
      showToast('Failed to remove: ' + e.message, 'error');
      load(); // restore on failure
    }
  }

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0 }}>Brand Images</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 14 }}>
            Images rotate round-robin with every post. The <strong>NEXT UP</strong> badge shows which posts next.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={isUploading}>
          {isUploading ? 'Uploading…' : '+ Upload Images'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
          borderRadius: 10, padding: '28px 20px', textAlign: 'center',
          marginBottom: 28, cursor: 'pointer',
          background: dragOver ? 'var(--primary-light, #eff6ff)' : 'transparent',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {isUploading ? 'Uploading…' : 'Drop images here or click to browse'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>JPEG, PNG, GIF · Unlimited storage</div>
      </div>

      {/* Upload progress bar */}
      {uploadQueue.length > 0 && (
        <div className="card" style={{ marginBottom: 24, padding: '14px 16px' }}>
          {/* Overall bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>
              {allDone ? '✅ Upload complete!' : `Uploading ${uploadQueue.filter(f => !f.done && !f.error).length} of ${uploadQueue.length} file${uploadQueue.length > 1 ? 's' : ''}…`}
            </span>
            <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600 }}>{overallPct}%</span>
          </div>
          <div style={{ height: 8, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{
              height: '100%', borderRadius: 99,
              background: allDone ? 'var(--success, #16a34a)' : 'var(--primary)',
              width: `${overallPct}%`,
              transition: 'width 0.2s ease, background 0.3s ease',
            }} />
          </div>
          {/* Per-file rows */}
          {uploadQueue.map(f => (
            <div key={f.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: f.error ? 'var(--danger,#dc2626)' : 'var(--text)' }}>
                {f.error ? `❌ ${f.name} — ${f.error}` : f.done ? `✅ ${f.name}` : `⏳ ${f.name}`}
              </span>
              {!f.error && (
                <div style={{ width: 80, height: 5, background: 'var(--border)', borderRadius: 99, overflow: 'hidden', flexShrink: 0 }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    background: f.done ? 'var(--success,#16a34a)' : 'var(--primary)',
                    width: `${f.done ? 100 : f.pct}%`,
                    transition: 'width 0.2s ease',
                  }} />
                </div>
              )}
              <span style={{ fontSize: 11, color: 'var(--muted)', width: 32, textAlign: 'right', flexShrink: 0 }}>
                {f.error ? '' : f.done ? '100%' : `${f.pct}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* How it works callout */}
      <div className="card" style={{ marginBottom: 24, padding: '12px 16px', background: 'var(--surface)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ fontSize: 20 }}>🔄</span>
        <div style={{ fontSize: 13 }}>
          <strong>How rotation works:</strong> Every time the post generator runs, it picks the image
          with the oldest &ldquo;last used&rdquo; date (images never used are always picked first).
          Once every image has been used, the cycle starts over automatically.
          If no images are uploaded, posts are published as text-only.
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>Loading…</div>
      ) : images.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48, color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No images yet</div>
          <div style={{ fontSize: 13 }}>Upload your first brand image above — posts will include it automatically.</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
            {images.length} image{images.length !== 1 ? 's' : ''} · rotation every ~4 hours
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 16,
          }}>
            {images.map(img => (
              <ImageCard key={img.image_key} img={img} onDelete={handleDelete} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
